import React, { createContext } from 'react'
import { BaseContextState, BaseContextProvider } from '../BaseContext'
import IBreed from '../../interfaces/IBreed'
import { SearchRequest, BreedSaveRequest, BreedDeleteRequest } from '../../services/api/interfaces/ApiRequest'
import { breedList, breedCreate, breedUpdate, breedDelete } from '../../services/api'

interface BreedContextState extends BaseContextState {
  breeds: Array<IBreed> | null,
  breed: IBreed | null,
}

export interface BreedContextData extends BreedContextState {
  resetErrors: Function,
  requestBreedList: (args: SearchRequest) => Promise<any>,
  requestBreedFind: (args: SearchRequest) => void,
  requestBreedCreate: (args: BreedSaveRequest) => Promise<any>,
  requestBreedUpdate: (args: BreedSaveRequest) => Promise<any>,
  requestBreedDelete: (args: BreedDeleteRequest) => Promise<any>
  lastListPayload: SearchRequest | null
}

// Create context
const { Provider, Consumer } = createContext({} as BreedContextData)

// Export consumer
export const BreedConsumer = Consumer

export class BreedProvider extends BaseContextProvider {
  state: BreedContextState = {
    breeds: null,
    breed: null
  }

  lastListPayload: SearchRequest | null = null

  /**
   * Make api request for breed list
   * 
   * @param {SearchRequest} payload 
   */
  requestBreedList = (payload: SearchRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        this.lastListPayload = payload
        const response = await breedList(payload)
  
        // Process our response
        this.processResponse(response, { 
          paginate: {
            destination: 'breeds'
          },
        })

        if (this.state.success) {
          resolve(this.state.breeds)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for breed finder
   * 
   * @param {SearchRequest} payload 
   */
  requestBreedFind = (payload: SearchRequest) => {
    this.setState({
      fetching: true,
      error: null,
      breed: null
    }, async () => {
      payload.options = {
        ...payload.options,
        _single: true
      }
      const response = await breedList(payload)

      // Process our response
      this.processResponse(response, { 
        mappings: {
          destination: 'breed'
        }
      })
    })
  }

  /**
   * Make api request for breed create
   * 
   * @param {BreedSaveRequest} payload 
   */
  requestBreedCreate = (payload: BreedSaveRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        let response = await breedCreate(payload)
        this.processResponse(response, {
          mappings: [
            { origin: 'breed', destination: 'breed' }
          ]
        })
  
        if (this.state.success) {
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for breed update
   * 
   * @param {BreedSaveRequest} payload 
   */
  requestBreedUpdate = (payload: BreedSaveRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        let response = await breedUpdate(payload)
        this.processResponse(response, {
          mappings: [
            { origin: 'breed', destination: 'breed' }
          ]
        })
  
        if (this.state.success) {
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for breed delete
   * 
   * @param {BreedDeleteRequest} payload 
   */
  requestBreedDelete = (payload: BreedDeleteRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        let response = await breedDelete(payload)
        this.processResponse(response)
  
        if (this.state.success) {
          this.requestBreedList(this.lastListPayload || {})
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  render() {
    return (
      <Provider
        value={{
          // States
          ...this.state,

          // Dispatches
          resetErrors: this.resetErrors,
          requestBreedList: this.requestBreedList,
          requestBreedFind: this.requestBreedFind,
          requestBreedCreate: this.requestBreedCreate,
          requestBreedUpdate: this.requestBreedUpdate,
          requestBreedDelete: this.requestBreedDelete,

          lastListPayload: this.lastListPayload,
        }}
      >
        {this.props.children}
      </Provider>
    )
  }
}
